/**
 * Sticky Add to Cart Block
 * Manages sticky cart visibility, product updates, and scroll behavior
 */

class StickyAddToCart {
  constructor() {
    this.stickyCart = document.getElementById('sticky-add-to-cart');
    this.stickyData = this.getStickyData();
    this.productContent = null;
    this.isVisible = false;
    this.scrollThreshold = 0;
    this.ticking = false;
    this.buyButtonWrapper = null;
    this.buyButtonObserver = null;
    this.isBuyButtonVisible = false;
    this.currentStickyCartPrice = null;

    // Selectors for dynamic updates
    this.selectors = {
      image: '[data-sticky-image] img',
      title: '[data-sticky-title]',
      pack: '[data-sticky-pack]',
      price: '[data-sticky-price]',
      perUnit: '[data-sticky-per-unit]',
      imageWrapper: '[data-sticky-image]'
    };

    this.init();
  }

  /**
   * Initialize the sticky cart
   */
  init() {
    if (!this.stickyCart) return;

    this.findProductContent();
    this.calculateScrollThreshold();
    this.setupBuyButtonObserver();
    this.bindEvents();
    this.updateStickyCart();

    // Set up quantity monitoring after initial update
    setTimeout(() => {
      this.observeQuantityChanges();
      // Initialize sticky cart price from Seal widget if available
      this.updateStickyCartPriceFromWidget();
    }, 100);

    if (this.isMobile()) {
      this.updateVisibility();
    }
  }

  /**
   * Check if device is mobile
   */
  isMobile() {
    return window.innerWidth < 768;
  }

  /**
   * Setup IntersectionObserver for buy button wrapper
   */
  setupBuyButtonObserver() {
    this.buyButtonWrapper = document.querySelector('.buy-button__wrapper');
    
    if (!this.buyButtonWrapper) {
      console.warn('Buy button wrapper not found');
      return;
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    this.buyButtonObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.isBuyButtonVisible = entry.isIntersecting;
        
        if (this.isMobile()) {
          this.updateVisibility();
        }
      });
    }, options);

    this.buyButtonObserver.observe(this.buyButtonWrapper);
  }

  /**
   * Get sticky cart data from script tag
   */
  getStickyData() {
    const dataScript = document.getElementById('sticky-cart-data');
    if (dataScript) {
      try {
        return JSON.parse(dataScript.textContent);
      } catch (e) {
        console.warn('Failed to parse sticky cart data:', e);
      }
    }
    return {};
  }

  /**
   * Find the product content element to track
   */
  findProductContent() {
    // Look for the main product container first
    this.productContent = document.querySelector('[data-product-main-container]');
    
    // Fallback to other common product selectors
    if (!this.productContent) {
      this.productContent = document.querySelector('.product-content, .product-template, #product-content');
    }
  }

  /**
   * Calculate when to show the sticky cart
   */
  calculateScrollThreshold() {
    if (this.productContent) {
      const rect = this.productContent.getBoundingClientRect();
      this.scrollThreshold = window.pageYOffset + rect.bottom - window.innerHeight * 0.3;
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Scroll tracking
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    window.addEventListener('resize', this.handleResize.bind(this), { passive: true });

    // Scroll to top button
    const scrollTopBtn = this.stickyCart?.querySelector('.sticky-cart__scroll-top');
    if (scrollTopBtn) {
      scrollTopBtn.addEventListener('click', this.scrollToTop.bind(this));
    }

    // Sticky add to cart button
    const stickyAddToCartBtn = this.stickyCart?.querySelector('[data-sticky-add-to-cart]');
    if (stickyAddToCartBtn) {
      stickyAddToCartBtn.addEventListener('click', this.handleStickyAddToCart.bind(this));
    }

    // Listen for product changes from stitching
    document.addEventListener('product:changed', this.handleProductChange.bind(this));

    // Listen for custom events that might be fired by stitching logic
    document.addEventListener('stitching:productUpdated', this.handleProductChange.bind(this));

    // Listen for Seal Subscriptions events
    document.addEventListener('sealsubs:price_update', this.handleSealSubsPriceUpdate.bind(this));
    document.addEventListener('sealsubs:selling_plan_changed', this.handleSealSubsSellingPlanChange.bind(this));
    document.addEventListener('sealsubs:subscription_widget_created', this.handleSealSubsWidgetCreated.bind(this));

    // Listen for quantity changes
    this.observeQuantityChanges();

    // Monitor changes in the main product form to sync sticky button text
    this.observeMainForm();
  }

  /**
   * Handle scroll events with throttling
   */
  handleScroll() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.updateVisibility();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    this.calculateScrollThreshold();
    
    if (!this.buyButtonObserver && this.isMobile()) {
      this.setupBuyButtonObserver();
    }
    
    this.updateVisibility();
  }

  /**
   * Update sticky cart visibility based on scroll position
   */
  updateVisibility() {
    let shouldShow = false;

    if (this.isMobile()) {
      shouldShow = !this.isBuyButtonVisible;
    } else {
      const scrollY = window.pageYOffset;
      shouldShow = scrollY > this.scrollThreshold;
    }

    if (shouldShow !== this.isVisible) {
      this.isVisible = shouldShow;
      this.stickyCart.classList.toggle('is-visible', shouldShow);
      
      // Dispatch custom event
      const event = new CustomEvent('stickyCart:visibilityChanged', {
        detail: { visible: shouldShow }
      });
      document.dispatchEvent(event);
    }
  }

  /**
   * Scroll smoothly to top of page
   */
  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    // Track the scroll to top action
    if (typeof gtag !== 'undefined') {
      gtag('event', 'scroll_to_top', {
        'event_category': 'sticky_cart',
        'event_label': 'button_click'
      });
    }
  }

  /**
   * Handle product changes from stitching functionality
   */
  handleProductChange(event) {
    let productData = null;

    // Try to get product data from event detail
    if (event.detail && event.detail.product) {
      productData = event.detail.product;
    } else if (event.detail && event.detail.handle) {
      // If we only have a handle, fetch product data
      this.fetchProductData(event.detail.handle);
      return;
    } else {
      // Try to get product data from the main product container
      productData = this.getCurrentProductData();
    }

    if (productData) {
      this.updateStickyCart(productData);
    }
  }

  /**
   * Get current product data from the page
   */
  getCurrentProductData() {
    const productContainer = document.querySelector('[data-product-main-container]');
    if (!productContainer) return null;

    // Get current product color
    const colorElement = productContainer.querySelector('[data-product-color]');
    const pdpColor = colorElement ? colorElement.textContent.trim() : '';

    // Get product title
    const titleElement = productContainer.querySelector('.product-title, h1');
    const title = titleElement ? titleElement.textContent.trim() : '';

    // Get product image
    const imageElement = productContainer.querySelector('.product-image img, .featured-image img');
    const image = imageElement ? imageElement.src : '';

    // Get product price
    const priceElement = productContainer.querySelector('.price, [data-product-price]');
    const price = priceElement ? priceElement.textContent.trim() : '';

    return {
      title,
      image,
      price,
      color: pdpColor
    };
  }

  /**
   * Update sticky cart with new product data
   */
  updateStickyCart(productData = null) {
    if (!this.stickyCart) return;

    const data = productData || this.getCurrentProductData();
    if (!data) return;

    // Update image
    if (data.image) {
      const imageEl = this.stickyCart.querySelector(this.selectors.image);
      if (imageEl) {
        imageEl.src = data.image;
        imageEl.alt = data.title || '';
      }
    }

    // Update title
    if (data.title) {
      const titleEl = this.stickyCart.querySelector(this.selectors.title);
      if (titleEl) {
        // Process title same way as Liquid: split by '-' first, then split by 'XOXO' and take last
        const processedTitle = this.processTitle(data.title);
        titleEl.textContent = processedTitle;
      }

      // Update pack info from title
      this.updatePackInfo(data.title);
    }

    // Update price
    if (data.price) {
      const priceEl = this.stickyCart.querySelector(this.selectors.price);
      if (priceEl) {
        priceEl.textContent = data.price;
      }

      // Update per unit price (will use current quantity from input)
      this.updatePerUnitPrice(data.title, data.price);
    }

    // Update background color
    if (data.color) {
      this.updateBackgroundColor(data.color);
    }

    // Update add to cart button price
    this.updateAddToCartButton(data.price);
  }

  /**
   * Process title same way as Liquid template
   * Liquid: {{ product.title | split: '-' | first | split: 'XOXO' | last }}
   */
  processTitle(title) {
    if (!title) return '';
    
    // Split by '-' and get first part
    const firstSplit = title.split('-')[0];
    
    // Split by 'XOXO' and get last part
    const parts = firstSplit.split('XOXO');
    const lastPart = parts[parts.length - 1];
    
    return lastPart.trim();
  }

  /**
   * Update pack information from product title
   */
  updatePackInfo(title) {
    if (!title) return;

    const packMatch = title.split(' - ').pop().match(/(\d+)\s*pack/i);
    const packSize = packMatch ? packMatch[1] : '1';
    
    const packEl = this.stickyCart.querySelector(this.selectors.pack);
    if (packEl) {
      packEl.textContent = `${packSize} Pack`;
    }
  }

  /**
   * Extract currency symbol from price string
   */
  extractCurrencySymbol(priceText) {
    if (!priceText) return '';
    // Match common currency symbols at the start
    const match = priceText.match(/^[£$€¥₹₽₩₪₫₨₦₡₨₩₪₫₨₦₡]/);
    if (match) return match[0];
    // Try to get from formatMoney if available
    if (typeof window.formatMoney === 'function') {
      // Test with a small amount to get currency format
      const testFormat = window.formatMoney(100);
      const currencyMatch = testFormat.match(/^[£$€¥₹₽₩₪₫₨₦₡₨₩₪₫₨₦₡]/);
      if (currencyMatch) return currencyMatch[0];
    }
    return '';
  }

  /**
   * Check if text contains any currency symbol
   */
  hasCurrencySymbol(text) {
    if (!text) return false;
    return /[£$€¥₹₽₩₪₫₨₦₡₨₩₪₫₨₦₡]/.test(text);
  }

  /**
   * Update per unit price calculation
   * Calculates: total price / (quantity × 12 cans per pack)
   */
  updatePerUnitPrice(title, price) {
    if (!price) return;

    // Get current quantity
    const quantityInput = document.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

    // Calculate total number of cans (quantity × 12 cans per pack)
    const totalCans = quantity * 12;

    // Extract currency symbol from price
    const currencySymbol = this.extractCurrencySymbol(price);

    // Extract numeric price value from the price string
    const priceMatch = price.replace(/[£$€¥₹₽₩₪₫₨₦₡₨₩₪₫₨₦₡,]/g, '').match(/[\d.]+/);
    if (priceMatch) {
      const totalPrice = parseFloat(priceMatch[0]);
      const perCanPrice = (totalPrice / totalCans).toFixed(2);
      
      const perUnitEl = this.stickyCart.querySelector(this.selectors.perUnit);
      if (perUnitEl) {
        perUnitEl.textContent = `${currencySymbol}${perCanPrice} per can`;
      }
    }
  }

  /**
   * Update background color based on product color
   */
  updateBackgroundColor(color) {
    if (!color || !color.includes('rgb')) return;

    // Extract RGB values
    const rgbMatch = color.match(/rgb\(([^)]+)\)/);
    if (!rgbMatch) return;

    const rgbValues = rgbMatch[1];
    this.stickyCart.setAttribute('data-product-color', rgbValues);

    // Update CSS custom property
    const sectionId = this.stickyCart.closest('[id^="shopify-section-"]')?.id || 'sticky-cart';
    let styleElement = document.querySelector(`style[data-sticky-section="${sectionId}"]`);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.setAttribute('data-sticky-section', sectionId);
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      .sticky-cart {
        --color-scheme-pdp-background: ${rgbValues};
      }
    `;
  }

  /**
   * Update add to cart button with current price
   */
  updateAddToCartButton(price) {
    if (!price) return;

    const buttonTextEl = this.stickyCart.querySelector('[data-sticky-button-text]');
    if (buttonTextEl) {
      // Format price if it's a number (assuming cents)
      let formattedPrice = price;
      if (typeof price === 'number') {
        formattedPrice = this.formatMoney(price);
      }
      buttonTextEl.textContent = `add to cart - ${formattedPrice}`;
    }
  }

  /**
   * Fetch product data for handle (if needed)
   */
  async fetchProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}?view=json`);
      if (response.ok) {
        const data = await response.json();
        this.updateStickyCart(data);
      }
    } catch (error) {
      console.warn('Failed to fetch product data for sticky cart:', error);
    }
  }

  /**
   * Handle sticky add to cart button click
   */
  handleStickyAddToCart(event) {
    event.preventDefault();
    
    // Find the main product form
    const mainProductForm = this.findMainProductForm();
    if (!mainProductForm) {
      console.warn('Main product form not found');
      return;
    }

    // Submit the main product form
    this.submitMainProductForm(mainProductForm);

    // Track the sticky cart usage
    if (typeof gtag !== 'undefined') {
      gtag('event', 'add_to_cart', {
        'event_category': 'sticky_cart',
        'event_label': 'button_click'
      });
    }
  }

  /**
   * Find the main product form on the page
   */
  findMainProductForm() {
    // Look for forms with data-product-form attribute
    const productForms = document.querySelectorAll('form[data-product-form]');
    
    // Return the first valid product form (should be the main one)
    for (const form of productForms) {
      if (form.querySelector('button[type="submit"], input[type="submit"]')) {
        return form;
      }
    }
    
    // Fallback to any form that looks like a product form
    return document.querySelector('form[action*="/cart/add"]') || 
           document.querySelector('form:has(button.add-to-cart-btn)') || 
           productForms[0] || 
           null;
  }

  /**
   * Submit the main product form with all its data
   */
  submitMainProductForm(form) {
    // Check if form has a submit button
    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
    
    if (submitBtn && !submitBtn.disabled) {
      // Click the original submit button to maintain all functionality
      // This ensures subscriptions, variants, and other app data are included
      submitBtn.click();
    } else if (!submitBtn) {
      // Fallback to direct form submission if no button found
      form.submit();
    }
  }

  /**
   * Observe changes in the main product form to sync data
   */
  observeMainForm() {
    const mainForm = this.findMainProductForm();
    if (!mainForm) return;

    // Watch for changes in the main form to update sticky button
    this.observeFormChanges(mainForm);
    
    // Initial sync
    this.syncWithMainForm(mainForm);
  }

  /**
   * Observe form changes and sync button text
   */
  observeFormChanges(form) {
    // Watch for subscription app changes and form state updates
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        // Check if subscription elements were added/modified or button state changed
        if (mutation.type === 'childList' || 
            (mutation.type === 'attributes' && 
             ['disabled', 'aria-label'].includes(mutation.attributeName))) {
          shouldUpdate = true;
        }
      });
      
      if (shouldUpdate) {
        setTimeout(() => this.syncWithMainForm(form), 100);
      }
    });

    // Start observing
    observer.observe(form, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-label', 'class']
    });

    // Store observer for cleanup
    this.formObserver = observer;
  }

  /**
   * Get price directly from Seal widget based on selected option
   */
  getPriceFromSealWidget() {
    const sealContainer = document.querySelector('.sls-purchase-options-container');
    if (!sealContainer) return null;

    // Check which option is selected
    const subscriptionOption = sealContainer.querySelector('.sls-option-container.sls-active[data-selling-plan-group]');
    const isSubscriptionSelected = subscriptionOption && subscriptionOption.classList.contains('sls-active');
    
    let priceText = null;
    
    if (isSubscriptionSelected) {
      // Get subscription price from widget
      const subscriptionPriceEl = sealContainer.querySelector('.sls-selling-plan-group-price .sls-price .money');
      if (subscriptionPriceEl) {
        priceText = subscriptionPriceEl.textContent.trim();
      }
    } else {
      // Get one-time price from widget
      const oneTimePriceEl = sealContainer.querySelector('.sls-one-time-price .money');
      if (oneTimePriceEl) {
        priceText = oneTimePriceEl.textContent.trim();
      }
    }

    return priceText;
  }

  /**
   * Update sticky cart price from Seal widget and store it
   */
  updateStickyCartPriceFromWidget() {
    const priceText = this.getPriceFromSealWidget();
    if (!priceText) return;

    // Update price display element
    const priceEl = this.stickyCart?.querySelector('[data-sticky-price]');
    if (priceEl) {
      priceEl.textContent = priceText;
    }

    // Update button text
    const buttonTextEl = this.stickyCart?.querySelector('[data-sticky-button-text]');
    if (buttonTextEl) {
      buttonTextEl.textContent = `add to cart - ${priceText}`;
    }

    // Update per can price
    this.updatePerUnitPrice(null, priceText);

    // Store the price for future restorations
    this.currentStickyCartPrice = priceText;
  }

  /**
   * Restore stored sticky cart price instantly
   */
  restoreStickyCartPrice() {
    if (!this.currentStickyCartPrice) return;

    // Update price display element
    const priceEl = this.stickyCart?.querySelector('[data-sticky-price]');
    if (priceEl) {
      priceEl.textContent = this.currentStickyCartPrice;
    }

    // Update button text
    const buttonTextEl = this.stickyCart?.querySelector('[data-sticky-button-text]');
    if (buttonTextEl) {
      buttonTextEl.textContent = `add to cart - ${this.currentStickyCartPrice}`;
    }

    // Update per can price
    this.updatePerUnitPrice(null, this.currentStickyCartPrice);
  }

  /**
   * Handle Seal Subscriptions price update events
   */
  handleSealSubsPriceUpdate(event) {
    // Store the price for quantity calculations
    const priceData = event.detail;
    if (priceData && priceData.price) {
      window.lastSealSubsPrice = priceData.price;
    }

    // Restore stored price IMMEDIATELY to prevent flicker
    this.restoreStickyCartPrice();
    
    // Also restore in next frame as backup
    requestAnimationFrame(() => {
      this.restoreStickyCartPrice();
    });
  }

  /**
   * Handle Seal Subscriptions selling plan change events
   */
  handleSealSubsSellingPlanChange(event) {
    // Update sticky cart price based on the NEWLY selected option
    this.updateStickyCartPriceFromWidget();
    
    // Also update in next frame as backup
    requestAnimationFrame(() => {
      this.updateStickyCartPriceFromWidget();
    });
  }

  /**
   * Handle Seal Subscriptions widget creation
   */
  handleSealSubsWidgetCreated(event) {
    // Update and store sticky cart price when widget is created
    setTimeout(() => {
      this.updateStickyCartPriceFromWidget();
    }, 100);
  }

  /**
   * Format price using Shopify's formatMoney function
   */
  formatMoney(priceInCents) {
    // Use the existing formatMoney function from utils.js
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(priceInCents);
    }

    // Fallback if formatMoney is not available - try to get currency from existing prices
    const existingPrice = document.querySelector('.money, [data-product-price], .price');
    let currencySymbol = '';
    if (existingPrice) {
      const priceText = existingPrice.textContent.trim();
      const currencyMatch = priceText.match(/^[£$€¥₹₽₩₪₫₨₦₡₨₩₪₫₨₦₡]/);
      if (currencyMatch) {
        currencySymbol = currencyMatch[0];
      }
    }
    
    const price = priceInCents / 100;
    return `${currencySymbol}${price.toFixed(2)}`;
  }

  /**
   * Observe quantity input changes
   */
  observeQuantityChanges() {
    // Prevent multiple initializations
    if (this.quantityObserversInitialized) {
      return;
    }

    this.quantityObserversInitialized = true;

    // Find quantity inputs in the product form
    const quantityInputs = document.querySelectorAll('input[name="quantity"]');

    quantityInputs.forEach((input) => {
      // Use a debounced handler to prevent multiple rapid calls
      let timeoutId;
      const debouncedHandler = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          this.handleQuantityChange();
        }, 100);
      };

      input.addEventListener('change', debouncedHandler);
      input.addEventListener('input', debouncedHandler);
    });

    // Also watch for quantity button clicks (+ and - buttons)
    const quantityButtons = document.querySelectorAll('button.quantity-button');

    quantityButtons.forEach((button) => {
      button.addEventListener('click', () => {
        // Delay to allow Alpine.js to update the quantity first
        setTimeout(() => {
          this.handleQuantityChange();
        }, 50);
      });
    });
  }

  /**
   * Handle quantity changes
   */
  handleQuantityChange() {
    // Get current quantity
    const quantityInput = document.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

    // Wait a bit for Seal widget to update prices, then read from widget
    setTimeout(() => {
      // Update sticky cart price from Seal widget (this will also store it)
      this.updateStickyCartPriceFromWidget();
    }, 100);
  }

  /**
   * Update sticky cart with total price (legacy method, kept for compatibility)
   */
  updateStickyCartWithTotal(totalPrice) {
    // Use the new method that reads from Seal widget
    this.updateStickyCartPriceFromWidget();
  }

  /**
   * Update main product page elements with quantity-based pricing
   */
  updateMainProductElements(totalPrice, quantity) {
    // Update main product price display to show quantity-based total
    this.updateMainProductPrice(totalPrice, quantity);

    // Note: We don't update the main add to cart button text to preserve styling
  }

  /**
   * Update the main product price display
   */
  updateMainProductPrice(totalPrice, quantity) {
    // Find the main product price elements
    const priceElements = document.querySelectorAll('.price, [data-product-price], .product-price');

    priceElements.forEach(element => {
      // Skip elements inside the sticky cart
      if (element.closest('#sticky-add-to-cart')) return;

      const currentText = element.textContent?.trim() || '';

      // If it contains a currency symbol and quantity > 1, update to show total
      if (this.hasCurrencySymbol(currentText) && quantity > 1) {
        const unitPrice = totalPrice / quantity;
        const formattedUnitPrice = this.formatMoney(unitPrice);
        const formattedTotalPrice = this.formatMoney(totalPrice);
        const newText = `${quantity} × ${formattedUnitPrice} = ${formattedTotalPrice}`;

        if (newText !== currentText) {
          element.textContent = newText;
        }
      } else if (quantity === 1) {
        // For quantity 1, show the unit price
        const unitPrice = totalPrice / quantity;
        const formattedUnitPrice = this.formatMoney(unitPrice);

        if (formattedUnitPrice !== currentText && this.hasCurrencySymbol(currentText)) {
          element.textContent = formattedUnitPrice;
        }
      }
    });
  }

  /**
   * Get current price per unit from various sources
   */
  getCurrentPricePerUnit() {
    // Try to get from Seal Subscriptions first
    const sealsubsElements = document.querySelectorAll('.sealsubs-target-element');

    for (const element of sealsubsElements) {
      // Check if Seal Subscriptions has stored price data
      if (element._sealsubs && element._sealsubs.price) {
        return element._sealsubs.price;
      }

      // Try to get price from data attributes
      if (element.dataset && element.dataset.price) {
        const price = parseInt(element.dataset.price);
        if (!isNaN(price)) {
          return price;
        }
      }
    }

    // Look for any recent Seal Subscriptions price_update events
    if (window.lastSealSubsPrice) {
      return window.lastSealSubsPrice;
    }

    // Try to get from sticky cart data
    const stickyData = this.getStickyData();
    if (stickyData && stickyData.price) {
      // Assume it's in cents if it's a number
      return typeof stickyData.price === 'number' ? stickyData.price : null;
    }

    // Fallback to regular product price - try multiple selectors
    const priceSelectors = [
      '.price',
      '[data-product-price]',
      '.product-price',
      '[class*="price"]',
      '.money'
    ];

    for (const selector of priceSelectors) {
      const priceElement = document.querySelector(selector);
      if (priceElement && !priceElement.closest('#sticky-add-to-cart')) {
        const priceText = priceElement.textContent.trim();

        // Extract numeric value (remove currency symbols and convert to cents)
        const priceMatch = priceText.replace(/[^\d.,]/g, '').replace(',', '.');
        const priceValue = parseFloat(priceMatch);

        if (!isNaN(priceValue) && priceValue > 0) {
          // Convert to cents (assuming the display is in currency units)
          return Math.round(priceValue * 100);
        }
      }
    }

    return null;
  }

  /**
   * Trigger Seal Subscriptions to recalculate based on quantity change
   */
  triggerSealSubscriptionsRecalculation() {
    try {
      // Method 1: Try to call Seal Subscriptions refresh method
      if (window.SealSubs && typeof window.SealSubs.refresh === 'function') {
        window.SealSubs.refresh();
      }

      // Method 2: Try to trigger form change event
      const quantityInput = document.querySelector('input[name="quantity"]');
      if (quantityInput) {
        const form = quantityInput.closest('form');
        if (form) {
          form.dispatchEvent(new Event('change', { bubbles: true }));
          form.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

    } catch (e) {
      console.warn('Failed to trigger Seal Subscriptions recalculation:', e);
    }
  }

  /**
   * Get current quantity
   */
  getCurrentQuantity() {
    const quantityInput = document.querySelector('input[name="quantity"]');
    return quantityInput ? parseInt(quantityInput.value) || 1 : 1;
  }


  /**
   * Sync sticky button with main form state
   */
  syncWithMainForm(form) {
    if (!form) return;

    const mainAddToCartBtn = form.querySelector('.add-to-cart-btn, button[type="submit"]');
    const stickyBtn = this.stickyCart?.querySelector('[data-sticky-add-to-cart]');
    const stickyBtnText = this.stickyCart?.querySelector('[data-sticky-button-text]');

    if (!mainAddToCartBtn || !stickyBtn) return;

    // Sync disabled state
    if (mainAddToCartBtn.disabled) {
      stickyBtn.setAttribute('disabled', 'disabled');
      stickyBtn.classList.add('cursor-not-allowed');
    } else {
      stickyBtn.removeAttribute('disabled');
      stickyBtn.classList.remove('cursor-not-allowed');
    }

    // Sync button text if it has changed (e.g., by subscription app or variant selection)
    // But don't override quantity-based pricing that includes "add to cart - [currency]"
    if (stickyBtnText && mainAddToCartBtn.textContent.trim()) {
      const mainBtnText = mainAddToCartBtn.textContent.trim();
      const currentStickyText = stickyBtnText.textContent.trim();

      // Check if current sticky text has quantity-based pricing format
      const hasQuantityBasedPricing = /add to cart - .*[£$€¥₹₽₩₪₫₨₦₡₨₩₪₫₨₦₡]/.test(currentStickyText);

      // Only update if different and not empty, and not overriding quantity-based pricing
      if (mainBtnText !== currentStickyText && mainBtnText.length > 0 && !hasQuantityBasedPricing) {
        stickyBtnText.textContent = mainBtnText;
      }
    }

    // Check for subscription-specific classes or attributes
    const hasSubscription = form.querySelector('[data-subscription], .seal-subscription, .subscription-widget');
    if (hasSubscription) {
      stickyBtn.classList.add('has-subscription');
      stickyBtn.setAttribute('data-has-subscription', 'true');
    } else {
      stickyBtn.classList.remove('has-subscription');
      stickyBtn.removeAttribute('data-has-subscription');
    }
  }

  /**
   * Destroy the sticky cart instance
   */
  destroy() {
    window.removeEventListener('scroll', this.handleScroll.bind(this));
    window.removeEventListener('resize', this.handleResize.bind(this));
    document.removeEventListener('product:changed', this.handleProductChange.bind(this));
    document.removeEventListener('stitching:productUpdated', this.handleProductChange.bind(this));
    
    if (this.formObserver) {
      this.formObserver.disconnect();
    }

    if (this.buyButtonObserver) {
      this.buyButtonObserver.disconnect();
    }
  }
}

// Initialize sticky cart when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize sticky cart
  window.stickyAddToCart = new StickyAddToCart();

  // Listen for stitching product updates and forward them
  if (window.productStitchingItems) {
    const originalFetchMethod = window.productStitchingItems.prototype.fetchSelectedProduct;
    
    if (originalFetchMethod) {
      window.productStitchingItems.prototype.fetchSelectedProduct = function(handle) {
        const result = originalFetchMethod.call(this, handle);
        
        // Dispatch custom event for sticky cart
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('stitching:productUpdated', {
            detail: { handle }
          }));
        }, 100);

        return result;
      };
    }
  }
});

// Export for potential use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = StickyAddToCart;
}
